// RadioGroup — the classic exclusive-choice list primitive, pure.
//
// One choice out of a vertical set, each option on its own row with a
// standard radio dial and a caller-owned label. Radiogroup semantics —
// exactly one selected, arrow keys move between options, the group is one
// tab stop (role="radiogroup"/role="radio"/aria-checked, roving tabindex) —
// are the hard-a11y contract we do NOT hand-roll; we wrap @radix-ui/react-
// radio-group, which owns it. This file only styles and shapes the surface.
//
// Sibling to SegmentedControl (same Radix primitive) but a different shape:
// SegmentedControl is the compact setting-toggle (Light/Dark); RadioGroup is
// the form-style list where each option needs room for a longer label and the
// selection reads as a filled dial. Two shapes, one contract.
//
// Register (.claude/research/design/06): near-monochrome. Selection is marked by the
// filled DOT (a glyph that is present only when checked) plus a darkened
// dial ring — two non-colour channels, so it survives SC 1.4.1 (colour is
// never the sole signal). No accent: choosing a form option is not "the
// primary action in this view", so the dial stays neutral ink; the dial's
// ring is the control's own shape, not a resting grouping border.
//
// Pure/props-driven: `value` + `onValueChange` make it fully controlled — no
// internal selection state, every change reported UP. `options`, `name`, and
// `disabled` arrive as props; no I/O, no domain knowledge. `ariaLabel` is
// required because a group of radios with no visible group label is
// unlabelled to AT, and the primitive authors no visible string of its own
// (labels are caller-owned ReactNodes) — so no i18n macro is needed here.

import * as Radix from "@radix-ui/react-radio-group";
import { type ReactNode, useId } from "react";
import "./RadioGroup.css";

// One selectable option. `value` is the identity reported through
// onValueChange; `label` is the visible content (any node — text, or text
// with a hint), which also names the radio for assistive tech.
export type RadioGroupOption = {
    readonly value: string;
    readonly label: ReactNode;
};

export type RadioGroupProps = {
    readonly options: readonly RadioGroupOption[];
    readonly value: string;
    readonly onValueChange: (value: string) => void;
    // The form field name shared by every radio in the group (genuine config,
    // not a meaning-swapping flag).
    readonly name: string;
    // Names the group for assistive tech (SC 4.1.2 / 1.3.1). Required.
    readonly ariaLabel: string;
    readonly disabled?: boolean;
};

export const RadioGroup = ({
    options,
    value,
    onValueChange,
    name,
    ariaLabel,
    disabled = false,
}: RadioGroupProps) => {
    // Stable base for per-option ids so each label binds to its own dial.
    const baseId = useId();
    return (
        <Radix.Root
            className={`bilda-radio${disabled ? " bilda-radio--disabled" : ""}`}
            value={value}
            onValueChange={onValueChange}
            name={name}
            aria-label={ariaLabel}
            disabled={disabled}
            // Wrap arrow-key nav from last↔first, the expected list behaviour.
            loop
        >
            {options.map((option) => {
                const itemId = `${baseId}-${option.value}`;
                return (
                    <label
                        key={option.value}
                        className="bilda-radio__option"
                        htmlFor={itemId}
                    >
                        <Radix.Item
                            id={itemId}
                            value={option.value}
                            className="bilda-radio__dial"
                        >
                            <Radix.Indicator className="bilda-radio__dot" />
                        </Radix.Item>
                        <span className="bilda-radio__label">
                            {option.label}
                        </span>
                    </label>
                );
            })}
        </Radix.Root>
    );
};
